import React from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { getPortalBaseUrl } from "@/utils/portalConfig";
import { buildPortalUrl, getPortalLoginHref } from "@/utils/portalHost";
import styles from "@/shared/components/layout/PublicFooter.module.css";

const providerGrowthHref = buildPortalUrl(
  getPortalBaseUrl(import.meta.env, "patient"),
  ROUTES.providersGrow,
);

type FooterLink = {
  label: string;
  href?: string;
  to?: string;
};

const footerColumns = [
  {
    heading: "ConfidaCare",
    links: [
      { label: "Home", to: ROUTES.home },
      { label: "About Us", to: ROUTES.aboutUs },
      { label: "Our Mission", to: ROUTES.about },
      { label: "Contact Us", to: ROUTES.contact },
      { label: "Help / FAQ", to: ROUTES.faq },
    ],
  },
  {
    heading: "Discover",
    links: [
      { label: "Find Care", to: ROUTES.home },
      { label: "Specialties", to: ROUTES.specialties },
      { label: "Home Remedies", to: ROUTES.homeRemedies },
      { label: "Insurance", to: ROUTES.insurance },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy Policy", to: ROUTES.privacy },
      { label: "Terms of Use", to: ROUTES.terms },
    ],
  },
  {
    heading: "Providers",
    links: [
      { label: "Join ConfidaCare", href: providerGrowthHref },
      { label: "Practice Resources", to: ROUTES.features },
      { label: "Provider Login", href: getPortalLoginHref("provider") },
      { label: "Contact support", to: `${ROUTES.contact}#providers` },
    ],
  },
] as const;

const PublicFooter: React.FC = () => {
  return (
    <footer className={styles.footer} aria-label="ConfidaCare footer">
      <div className={styles.inner}>
        <div className={styles.top}>
          <div className={styles.brandBlock}>
            <Link to={ROUTES.home} className={styles.brandLogo} aria-label="ConfidaCare home">
              <img src="/images/confida-care-logo.png" alt="ConfidaCare" />
            </Link>
            <p className={styles.brandText}>
              ConfidaCare helps patients discover care with more clarity and helps
              providers connect with the people they serve through privacy-aware,
              healthcare-friendly workflows.
            </p>
          </div>

          <div className={styles.grid}>
            {footerColumns.map((column) => (
              <section key={column.heading} className={styles.column} aria-label={column.heading}>
                <h2 className={styles.heading}>{column.heading}</h2>
                <ul className={styles.links}>
                  {column.links.map((link) => (
                    <li key={link.label}>
                      {"href" in link && link.href ? (
                        <a href={link.href} className={styles.link}>
                          {link.label}
                        </a>
                      ) : (
                        <Link to={(link as FooterLink).to ?? ROUTES.home} className={styles.link}>
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>

        <div className={styles.contactBar} aria-label="Contact details">
          <span className={styles.contactLabel}>Contact</span>
          <div className={styles.contactInline}>
            <span className={styles.contactItem}>
              <span className={styles.contactKey}>Email:</span>
              <a className={styles.contactValue} href="mailto:support@confidacare.com">
                support@confidacare.com
              </a>
            </span>
            <span className={styles.contactDivider} aria-hidden="true">|</span>
            <span className={styles.contactItem}>
              <span className={styles.contactKey}>Phone:</span>
              <a className={styles.contactValue} href="tel:+18723624357">
                (872) 362-4357
              </a>
            </span>
            <span className={styles.contactDivider} aria-hidden="true">|</span>
            <span className={styles.contactItem}>
              <span className={styles.contactValue}>872-DOC-HELP</span>
            </span>
          </div>
        </div>

        <div className={styles.bottom}>
          <p className={styles.copyright}>{"\u00A9"} 2026 ConfidaCare. All rights reserved.</p>
          <p className={styles.disclaimer}>
            ConfidaCare helps patients discover and connect with healthcare providers.
            ConfidaCare is not a medical provider and does not provide medical advice,
            diagnosis, or treatment.
          </p>
        </div>
      </div>
    </footer>
  );
};

export { PublicFooter };
